// src/features/hmi-simulator/components/screens/MainScreen.tsx
import React from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  CircularProgress
} from '@mui/material';
import { QrCode, Person } from '@mui/icons-material';
import { Header } from '../shared/Header';
import { HMIConfig } from '../../types';

interface MainScreenProps {
  config: HMIConfig;
  language: string;
  setLanguage: (lang: string) => void;
  isOnline: boolean;
  apiLoading: boolean;
  t: (key: string) => string;
  api: any;
  setScreen: (screen: any) => void;
  email: string;
  setEmail: (value: string) => void;
  accessCode: string;
  setAccessCode: (value: string) => void;
  message: string;
  error: string;
  onEmailSubmit: () => void;
  onCodeSubmit: () => void;
}

export const MainScreen: React.FC<MainScreenProps> = ({
  config,
  language,
  setLanguage,
  isOnline,
  apiLoading,
  t,
  api,
  setScreen,
  email,
  setEmail,
  accessCode,
  setAccessCode,
  message,
  error,
  onEmailSubmit,
  onCodeSubmit
}) => {
  const handleEmailKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && email) {
      onEmailSubmit();
    }
  };

  const handleCodeKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && accessCode) {
      onCodeSubmit();
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <Header
        config={config}
        language={language}
        setLanguage={setLanguage}
        isOnline={isOnline}
        deviceId={api.getCredentials()?.deviceId}
        onSettingsClick={() => setScreen('settings')}
      />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* Sección: Necesita una taquilla */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {t('need_locker_title')}
                </Typography>
                <Typography color="text.secondary" paragraph>
                  {t('email_instructions')}
                </Typography>
                
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={handleEmailKeyPress}
                    placeholder={t('email_placeholder')}
                    autoComplete="email"
                  />
                  
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={onEmailSubmit}
                    disabled={!email || apiLoading}
                    sx={{ backgroundColor: config.primaryColor }}
                  >
                    {apiLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      t('get_code_button')
                    )}
                  </Button>
                  
                  {message && <Alert severity="success">{message}</Alert>}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Sección: Tiene un código */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {t('have_code_title')}
                </Typography>
                <Typography color="text.secondary" paragraph>
                  {t('code_instructions')}
                </Typography>
                
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Access Code"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    onKeyPress={handleCodeKeyPress}
                    placeholder={t('code_placeholder')}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                  />
                  
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={onCodeSubmit}
                    disabled={!accessCode || apiLoading}
                    sx={{ backgroundColor: config.primaryColor }}
                  >
                    {apiLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      t('submit_button')
                    )}
                  </Button>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: config.primaryColor }}>
                    <QrCode />
                    <Typography variant="body2">Or scan QR code from email</Typography>
                  </Box>
                  
                  {error && <Alert severity="error">{error}</Alert>}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            startIcon={<Person />}
            onClick={() => setScreen('staff')}
            variant="outlined"
          >
            {t('staff_login')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};